import { syncDateLocaleWithI18n } from '../helpers';
import { config } from '../config/config';
import { i18next } from './i18n';
import { getTranslationOverrides } from './translationOverrides';
import { APP_LANGUAGES, getAppLanguage, LANGUAGE_NAME_OVERRIDES } from './languages';

// Type for translation object
type TranslationData = Record<string, string | Record<string, string>>;

// Type for translation URL provider function
type TranslationUrlProvider = () => Record<string, string>;

// Cache for remote translation files
const translationCache = new Map<string, TranslationData>();
const loadingPromises = new Map<string, Promise<TranslationData | null>>();

// Provider for translation URLs - will be set by platform-specific implementation
let translationUrlProvider: TranslationUrlProvider | null = null;

/**
 * Sets the translation URL provider for the current platform
 * This should be called during app initialization with the appropriate provider
 */
export const setTranslationUrlProvider = (
  provider: TranslationUrlProvider
): void => {
  translationUrlProvider = provider;
};

/**
 * Gets the translation URLs from the current provider
 * Falls back to environment-specific defaults if no provider is set
 */
const getTranslationUrls = (): Record<string, string> => {
  const documentsBaseUrl =
    config.NX_PUBLIC_ENV === 'prod'
      ? 'https://documents.carecircle.org'
      : config.NX_PUBLIC_ENV === 'test'
        ? 'https://documents.test.carecircle.org'
        : 'https://documents.dev.carecircle.org';

  const defaultUrlsByLanguagePath = Object.fromEntries(
    APP_LANGUAGES.filter(({ shortCode }) => shortCode !== 'en')
      .map(({ code, shortCode }) => [code, `/locales/${shortCode}/${code.toLowerCase()}.json`])
  );

  const defaultEnvUrls = Object.fromEntries(
    Object.entries(defaultUrlsByLanguagePath).map(([lang, path]) => [
      lang,
      `${documentsBaseUrl}${path}`,
    ])
  ) as Record<string, string>;

  if (!translationUrlProvider) {
    // Fallback to environment-specific defaults if no provider is set.
    console.warn(
      'No translation URL provider set, using default environment URLs'
    );
    return defaultEnvUrls;
  }

  // Provider may be configured but environment variables can be missing in
  // certain deployments. Filter out any falsy URLs and fall back for missing entries.
  const provided = translationUrlProvider();
  const providedFiltered = Object.fromEntries(
    Object.entries(provided).filter(([, url]) => typeof url === 'string' && url)
  ) as Record<string, string>;

  if (Object.keys(providedFiltered).length === 0) {
    console.warn(
      'Translation URL provider returned no valid URLs; using default environment URLs'
    );
    return defaultEnvUrls;
  }

  // Merge defaults for any missing languages so language switching continues to work.
  return {
    ...defaultEnvUrls,
    ...providedFiltered,
  };
};

// Map full language codes to i18next short codes
const LANGUAGE_CODE_MAP = Object.fromEntries(
  APP_LANGUAGES.map(({ code, shortCode }) => [code, shortCode])
);

/**
 * Applies translation overrides for the given short language code.
 * Overrides are merged on top of the existing resource bundle so that
 * social-network-specific terms always take precedence over remote files.
 */
const applyOverrides = (shortCode: string): void => {
  const overrides = getTranslationOverrides(shortCode);
  if (overrides) {
    i18next.addResourceBundle(shortCode, 'translation', overrides, true, true);
  }
  i18next.addResourceBundle(shortCode, 'translation', LANGUAGE_NAME_OVERRIDES, true, true);
};

/**
 * Loads a translation file from remote URL and caches it
 */
const loadRemoteTranslation = async (
  languageCode: string
): Promise<TranslationData | null> => {
  const translationUrls = getTranslationUrls();
  const url = translationUrls[languageCode];

  if (!url) {
    console.warn(`No remote URL configured for language: ${languageCode}`);
    return null;
  }

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(
        `Failed to load translation for ${languageCode}: ${response.status}`
      );
    }

    const translation = await response.json();
    translationCache.set(languageCode, translation);
    return translation;
  } catch (error) {
    console.error(`Error loading translation for ${languageCode}:`, error);
    return null;
  }
};

/**
 * Ensures a language is loaded into i18next, loading remotely if needed
 */
export const ensureLanguageLoaded = async (
  languageCode: string
): Promise<boolean> => {
  languageCode = getFullLanguageCode(languageCode);
  const shortCode =
    LANGUAGE_CODE_MAP[languageCode as keyof typeof LANGUAGE_CODE_MAP] ||
    languageCode;

  // Check if language is already loaded in i18next
  if (i18next.hasResourceBundle(shortCode, 'translation')) {
    return true;
  }

  // For English, it should already be loaded from local file
  if (languageCode === 'en-GB' || shortCode === 'en') {
    return true;
  }

  // Check if we already have it in cache
  if (translationCache.has(languageCode)) {
    const translation = translationCache.get(languageCode);
    i18next.addResourceBundle(shortCode, 'translation', translation);
    applyOverrides(shortCode);
    return true;
  }

  // Check if we're already loading this language
  if (loadingPromises.has(languageCode)) {
    const translation = await loadingPromises.get(languageCode);
    if (translation) {
      i18next.addResourceBundle(shortCode, 'translation', translation);
      applyOverrides(shortCode);
      return true;
    }
    return false;
  }

  // Load the translation
  const loadingPromise = loadRemoteTranslation(languageCode);
  loadingPromises.set(languageCode, loadingPromise);

  try {
    const translation = await loadingPromise;
    if (translation) {
      i18next.addResourceBundle(shortCode, 'translation', translation);
      applyOverrides(shortCode);
      return true;
    }
    return false;
  } finally {
    loadingPromises.delete(languageCode);
  }
};

/**
 * Changes language with remote loading support
 */
export const changeLanguageWithRemoteSupport = async (
  languageCode: string
): Promise<boolean> => {
  languageCode = getFullLanguageCode(languageCode);
  const shortCode =
    LANGUAGE_CODE_MAP[languageCode as keyof typeof LANGUAGE_CODE_MAP] ||
    languageCode;

  try {
    // For English, just change language directly
    if (languageCode === 'en-GB' || shortCode === 'en') {
      await i18next.changeLanguage(shortCode);
      syncDateLocaleWithI18n(shortCode);
      return true;
    }

    // Check if translation is already cached for faster switching
    if (translationCache.has(languageCode)) {
      const translation = translationCache.get(languageCode);
      if (!i18next.hasResourceBundle(shortCode, 'translation')) {
        i18next.addResourceBundle(shortCode, 'translation', translation);
      }
      applyOverrides(shortCode);
      await i18next.changeLanguage(shortCode);
      syncDateLocaleWithI18n(shortCode);
      return true;
    }

    // Ensure the language is loaded (will fetch if not cached)
    const loaded = await ensureLanguageLoaded(languageCode);
    if (!loaded) {
      console.warn(`Failed to load language: ${languageCode}`);
      return false;
    }

    // Change to the language
    await i18next.changeLanguage(shortCode);
    syncDateLocaleWithI18n(shortCode);
    return true;
  } catch (error) {
    console.error(`Error changing language to ${languageCode}:`, error);
    return false;
  }
};

/**
 * Preloads all configured remote translations for better UX
 */
export const preloadRemoteTranslations = async (): Promise<void> => {
  const translationUrls = getTranslationUrls();
  const languages = Object.keys(translationUrls);

  const loadPromises = languages.map(async (lang) => {
    try {
      await ensureLanguageLoaded(lang);
    } catch (error) {
      console.warn(`Failed to preload language ${lang}:`, error);
    }
  });

  await Promise.allSettled(loadPromises);
};

/**
 * Checks if all remote translations are preloaded and cached
 */
export const areTranslationsPreloaded = (): boolean => {
  const translationUrls = getTranslationUrls();
  const languages = Object.keys(translationUrls);
  return languages.every((lang) => translationCache.has(lang));
};

/**
 * Maps full language codes to short codes used by i18next
 */
export const getShortLanguageCode = (fullCode: string): string => {
  return getAppLanguage(fullCode)?.shortCode ?? fullCode;
};

/**
 * Maps short language codes back to full codes
 */
export const getFullLanguageCode = (shortCode: string): string => {
  return getAppLanguage(shortCode)?.code ?? shortCode;
};
